import {
	Alert,
	Box,
	Button,
	Container,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'
import { FC, useEffect, useState } from 'react'

import ApproverOverview from '../../../ControlledFormInputs/ApproverOverview'
import ReadGenericTagsGCP from '../../../ControlledFormInputs/GenericTagsReadGCP'
import { getApplicationFundingApprovals } from '../../../../helper/applicationTagApproval'
import { clusterEnteredOwnFunding } from '../../../../helper/applicationTagInheritance'
import useSingleTags from '../../../../hooks/query/useSingleTags'
import GcpPlatformTagsNew from '../../../../pages/platforms/new/GcpPlatformNewTags'
import { useNewApplicationFormStore } from '../../../../stores/applications/newApplicationFormStore'
import { useGenericTagsManagementFormStore } from '../../../../stores/tags/genericTagsManagementFormStore'

type ApplicationTagsProps = {
	registerValidation?: (validate: () => Promise<boolean>) => void
}

const fundingFields = [
	'platformName',
	'group',
	'fundingSourceType',
	'WBSCode',
	'costCenter',
	'dataClassification',
	'projectName',
	'systemAlias',
	'platformOwner'
] as const

const pickFundingDetails = (details: Record<string, any> | undefined) => {
	const picked: Record<string, any> = {}
	fundingFields.forEach((field) => {
		if (details?.[field] !== undefined) {
			picked[field] = details[field]
		}
	})
	return picked
}

const ApplicationTags: FC<ApplicationTagsProps> = ({ registerValidation }) => {
	const [isUsingInheritedTags, setIsUsingInheritedTags] = useState(true)
	const {
		formValues,
		actions: { setFormValues }
	} = useNewApplicationFormStore()
	const {
		actions: { clearFormValues: clearTagFormValues, replaceFormValues }
	} = useGenericTagsManagementFormStore()
	const resolution = formValues.gcpProjectID?.resolution
	const sourceTag = resolution?.selectedProject?.tags
	const projectTagsId = resolution?.inheritedTagsID || sourceTag?.tagsID || ''
	const projectDetails = resolution?.inheritedTagDetails || sourceTag?.details
	const cluster = resolution?.resolvedCluster
	const clusterOwnsFunding = clusterEnteredOwnFunding(cluster, projectTagsId)
	const sourceTagsId = clusterOwnsFunding ? cluster?.tagsID || '' : projectTagsId
	const projectLabel =
		formValues.gcpProjectID?.label ||
		resolution?.selectedProject?.gcpProjectName ||
		''
	const { data: clusterTag, isLoading: isClusterTagLoading } = useSingleTags(
		cluster?.tagsID || '',
		{ enabled: clusterOwnsFunding }
	)
	const sourceDetails = clusterOwnsFunding
		? clusterTag?.details
		: projectDetails
	const sourceReady = Boolean(
		sourceTagsId && sourceDetails && !(clusterOwnsFunding && isClusterTagLoading)
	)
	const applicationApprovalPreview = getApplicationFundingApprovals(
		formValues.applicationName
	)

	useEffect(() => {
		if (!isUsingInheritedTags || !sourceReady || !sourceDetails) {
			return
		}

		replaceFormValues(pickFundingDetails(sourceDetails))
		setFormValues('inheritGcpTagsBool', true)
		setFormValues('parentTagsID', sourceTagsId)
		setFormValues('wbsCode', sourceDetails.WBSCode || '')
		setFormValues('costCenter', sourceDetails.costCenter || '')
		setFormValues('group', sourceDetails.group || '')
	}, [
		isUsingInheritedTags,
		replaceFormValues,
		setFormValues,
		sourceDetails,
		sourceReady,
		sourceTagsId
	])

	useEffect(() => {
		if (!isUsingInheritedTags) {
			return
		}

		registerValidation?.(async () => sourceReady)
	}, [isUsingInheritedTags, registerValidation, sourceReady])

	const switchToNewTags = () => {
		setIsUsingInheritedTags(false)
		setFormValues('inheritGcpTagsBool', false)
		setFormValues('parentTagsID', sourceTagsId)
	}

	const switchToInheritedTags = () => {
		setIsUsingInheritedTags(true)
		setFormValues('inheritGcpTagsBool', true)
		clearTagFormValues()
	}

	return (
		<Container
			header={
				<Header
					variant="h2"
					actions={
						sourceTagsId && (
							<SpaceBetween direction="horizontal" size="xs">
								{isUsingInheritedTags ? (
									<Button variant="link" onClick={switchToNewTags}>
										+ Enter funding
									</Button>
								) : (
									<Button variant="link" onClick={switchToInheritedTags}>
										- Use inherited funding
									</Button>
								)}
							</SpaceBetween>
						)
					}
				>
					Resource Tags
				</Header>
			}
		>
			<SpaceBetween direction="vertical" size="l">
				{!sourceTagsId && (
					<Alert type="warning">
						Select an available GCP Project in Application Details to copy its funding.
					</Alert>
				)}
				{clusterOwnsFunding && isClusterTagLoading && (
					<Alert type="info">Loading the cluster funding.</Alert>
				)}
				{isUsingInheritedTags && sourceReady && (
					<>
						<ReadGenericTagsGCP
							tagsId={sourceTagsId}
							showApprovalStatus={false}
						/>
						<div>
							<Box variant="awsui-key-label">Application Owner</Box>
							{formValues.applicationOwner || '-'}
						</div>
						<ApproverOverview
							approvalStatus={applicationApprovalPreview}
							isDisabled
						/>
					</>
				)}
				{!isUsingInheritedTags && (
					<>
						<GcpPlatformTagsNew
							registerValidation={registerValidation}
							projectLabel={projectLabel}
						/>
						<ApproverOverview
							approvalStatus={applicationApprovalPreview}
							isDisabled
						/>
					</>
				)}
			</SpaceBetween>
		</Container>
	)
}

export default ApplicationTags
